'use client';

import { useState, useEffect, useTransition, useRef, useCallback, FC } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import SharedNavbar from '@/hotdesk-ui/components/shared/Navbar';
import { Languages } from '../../constants/langulage.constant';
import { getDisplayLanguageCode } from '@/hotdesk-ui/lib/language.service';
import { useChangeLanguage } from '@/hotdesk-ui/hooks/useChangeLanguage';
import { firebaseAnalytics, clearItidaCustomSignal } from '@/hotdesk-ui/common-classes/firebase.service';
import { RootState } from '@/hotdesk-ui/store/store';
import { useAppDispatch } from '@/hotdesk-ui/hooks/hook';
import { useSubscriptionAvailability } from '@/hotdesk-ui/contexts/SubscriptionAvailabilityContext';
import { logout } from '@/hotdesk-ui/store/slices/auth.slice';
import { setGeoLocation, toggelLocationPopup } from '@/hotdesk-ui/store/slices/geo-location.slice';
import { setCurrentSearch } from '@/hotdesk-ui/store/slices/search.slice';
import { getWorkspaceData } from '@/hotdesk-ui/store/slices/listing.slice';
import { useSelector } from 'react-redux';
import { AuthPopupService } from '@/hotdesk-ui/services/authPopup.service';
import { Carousel } from '@/hotdesk-ui/enum/listing-workspace';
import { getDefaultLocation } from '@/hotdesk-ui/helpers/location.helper';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import { StorageKeys } from '@/hotdesk-ui/enum/storage-keys';
import { storeSet } from '@/hotdesk-ui/helpers/utils';
import { appendExploreLocationParams } from '@/hotdesk-ui/helpers/explore-location-params';
interface NavbarClientWrapperProps {
  languageCode: string;
  isDirectory: boolean;
  style: string;
  isBannerVisible: boolean;
}
const DarkNavbarClientWrapper: FC<NavbarClientWrapperProps> = ({
  languageCode,
  isDirectory,
  style,
  isBannerVisible
}) => {
  const router = useRouter();
  const popService = new AuthPopupService();
  const user = useSelector((state: RootState) => state.auth.user);
  const geoLocation = useSelector((state: RootState) => state.geoLocation.geoLocationZipData);
  const searchParams = useSearchParams();
  const showAuthSkeleton = searchParams?.get('type') === 'magic_link' && !user;
  const [language, setLanguage] = useState(`language.${languageCode}`);
  const {
    status: subscriptionAvailabilityStatus
  } = useSubscriptionAvailability();
  const subscriptionLink = subscriptionAvailabilityStatus === 'available';
  const [listingType, setListingType] = useState<number>(0);
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const [, startTransition] = useTransition();

  // Search refs for the navbar search modal
  const autocompleteInput = useRef<HTMLInputElement>(null);
  const autocompleteContainer = useRef<HTMLDivElement>(null);
  const languageOptions = Languages.map(lang => ({
    value: lang.code,
    label: lang.displayText
  }));
  const handleLanguageChange = useChangeLanguage(setLanguage);
  useEffect(() => {
    startTransition(async () => {
      setLanguage(await getDisplayLanguageCode());
    });
  }, []);

  // Prefetch the navbar destinations so router.push() doesn't pause on the
  // root spinner while waiting for the route's chunks (and its loading.tsx)
  // to load. Account routes are prefetched when the user is signed in.
  useEffect(() => {
    router.prefetch('/');
    router.prefetch('/home');
    router.prefetch('/workspaces');
    if (subscriptionLink) {
      router.prefetch('/memberships');
    }
  }, [router, subscriptionLink]);
  useEffect(() => {
    if (!user) return;
    router.prefetch('/my-bookings');
    router.prefetch('/my-account');
    router.prefetch('/favorites');
  }, [router, user]);

  // ─── Handlers ─────────────────────────────────────────────
  const signIn = async () => {
    const screen = pathname === '/' ? 'landing' : pathname === '/workspaces' ? 'explore' : pathname.startsWith('/workspaces') ? 'workspace' : pathname.replace('/', '');
    await firebaseAnalytics('web_auth_entry', {
      screen
    });
    popService.getPopup();
  };
  const signOut = async () => {
    dispatch(logout());
    await clearItidaCustomSignal();
    window.location.href = '/';
  };
  const moveToRoute = (eventName?: string) => {
    if (eventName) {
      firebaseAnalytics(eventName, {
        source: 'header'
      });
    }
  };
  const scrollToSection = async (sectionId: string, eventName: string) => {
    const doScroll = () => {
      document.querySelector(sectionId)?.scrollIntoView({
        behavior: 'smooth'
      });
    };
    if (pathname === '/' || pathname === '/home') {
      doScroll();
    } else {
      await router.push('/home');
      setTimeout(doScroll, 300);
    }
    firebaseAnalytics(eventName, {
      source: 'header'
    });
  };
  const osClick = () => {
    firebaseAnalytics('for_workspaces_clicked', {
      source: 'header'
    });
  };

  // Search modal — if permission is granted, fill input with current address; otherwise show popup
  const getPermission = () => {
    if ('geolocation' in navigator) {
      navigator.permissions.query({
        name: 'geolocation'
      }).then(ps => {
        if (ps.state === 'granted') {
          navigator.geolocation.getCurrentPosition(({
            coords
          }) => {
            getDefaultLocation(coords.latitude, coords.longitude, (result: GeoCodeResult) => {
              if (autocompleteInput.current) {
                autocompleteInput.current.value = result.formatted_address;
              }
              result.lat = coords.latitude;
              result.log = coords.longitude;
              dispatch(setGeoLocation(result));
              dispatch(setCurrentSearch(result));
              dispatch(getWorkspaceData({
                lastSearch: result,
                page: 1
              }));
              storeSet(StorageKeys.GEO_LOCATION_DATA, result);
            });
          });
        } else {
          dispatch(toggelLocationPopup(true));
        }
      });
    } else {
      dispatch(toggelLocationPopup(true));
    }
  };
  const sentData = (typeOverride?: number) => {
    const params = new URLSearchParams({
      lang: languageCode
    });
    const type = typeOverride !== undefined ? typeOverride : listingType;
    if (type && type !== 0) {
      params.set('typeIn', type.toString());
      params.set('active', Carousel.listing);
    }
    appendExploreLocationParams(params, geoLocation);
    firebaseAnalytics('search_clicked', {
      source: 'navbar'
    });
    router.push(`/workspaces?${params}`);
  };
  const addFilters = (value: number) => {
    setListingType(value);
  };

  // A place picked in the header search. Mirrors the landing hero search:
  // remember the location, then open explore on it. Works from any page,
  // which is the point of the header owning a search bar.
  const handleSearchSelect = useCallback((result: GeoCodeResult) => {
    // Google predictions carry no source; "Nearby" arrives already
    // marked 'user-geolocation' and must keep it (explore shows
    // "Nearby" for that source).
    result.source = result.source ?? 'autocomplete';
    dispatch(setGeoLocation(result));
    dispatch(setCurrentSearch(result));
    storeSet(StorageKeys.GEO_LOCATION_DATA, result);
    const params = new URLSearchParams({
      lang: languageCode
    });
    if (listingType) {
      params.set('typeIn', listingType.toString());
      params.set('active', Carousel.listing);
    }
    appendExploreLocationParams(params, result);
    firebaseAnalytics('search_clicked', {
      source: 'header'
    });
    router.push(`/workspaces?${params}`);
  }, [dispatch, languageCode, listingType, router]);
  return <SharedNavbar isDirectory={isDirectory} languageCode={language} scrollToSection={scrollToSection} moveToRoute={moveToRoute} handleChange={handleLanguageChange} osClick={osClick} style={style} signIn={signIn} signOut={signOut} login={!!user} user={user} availableLanguages={languageOptions} pathname={pathname} subscriptionLink={subscriptionLink} autocompleteInput={autocompleteInput as any} autocompleteContainer={autocompleteContainer as any} getPermission={getPermission} sentData={sentData} listingType={listingType} addFilters={addFilters} showAuthSkeleton={showAuthSkeleton} onSearchSelect={handleSearchSelect} data-sentry-element="SharedNavbar" data-sentry-component="DarkNavbarClientWrapper" data-sentry-source-file="DarkNavbarClientWrapper.tsx" />;
};
export default DarkNavbarClientWrapper;